import api from './api';

class WalletSyncService {
  /**
   * Sync a wallet using the generic server-side sync
   * @param {number} walletId - The wallet ID to sync
   * @returns {Promise<Object>} Sync result with balances and transaction counts
   */
  async syncWallet(walletId) {
    try {
      const response = await api.post(`/api/wallets/${walletId}/sync`);
      return response.data;
    } catch (error) {
      console.error('Error syncing wallet:', error);
      throw error;
    }
  }

  /**
   * Get grouped balances for all user wallets
   * @param {Object} options - Options object
   * @param {boolean} options.fresh - If true, triggers background sync before returning data
   * @returns {Promise<Array>} Array of grouped balances by coin
   */
  async getGroupedBalances(options = {}) {
    try {
      // Call the backend service method directly instead of the API endpoint
      // This ensures we get the grouped balances with total_balance field
      const response = await api.get('/api/wallets/balances', {
        params: options
      });
      
      // If the response is individual balances, we need to group them
      if (response.data && response.data.length > 0 && response.data[0].balance) {
        // Group individual balances by coin
        const grouped = {};
        response.data.forEach(item => {
          if (!grouped[item.coin]) {
            grouped[item.coin] = {
              coin: item.coin,
              total_balance: 0,
              wallet_count: 0,
              coin_name: item.coin_name,
              coingecko_id: item.coingecko_id,
              image_url: item.image_url,
              image_url_2: item.image_url_2,
              image_url_3: item.image_url_3,
              token_name: item.token_name,
              token_description: item.token_description,
              avg_buy_price: null,
              is_avg_price_estimated: false
            };
          }
          grouped[item.coin].total_balance += parseFloat(item.balance) || 0;
          grouped[item.coin].wallet_count += 1;
          
          // Use the first non-null avg_buy_price for this coin
          if (item.avg_buy_price && grouped[item.coin].avg_buy_price === null) {
            grouped[item.coin].avg_buy_price = parseFloat(item.avg_buy_price);
            grouped[item.coin].is_avg_price_estimated = item.is_avg_price_estimated === 1 || item.is_avg_price_estimated === true;
          }
        });
        
        return Object.values(grouped);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching grouped balances:', error);
      throw error;
    }
  }

  /**
   * Fetch transactions with pagination
   * @param {number} limit
   * @param {number} offset
   * @param {object} filters
   * @returns {Promise<object>} Object with transactions and pagination info
   */
  async getTransactions(limit = 50, offset = 0, filters = {}) {
    try {
      const params = { limit, offset, ...filters };
      const response = await api.get('/api/wallets/transactions', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  /**
   * Add a new wallet (was addWalletWithEnhancedSync)
   * @param {Object} walletData - Wallet data {blockchain, address, label}
   * @returns {Promise<Object>} Created wallet with sync result
   */
  async addWallet(walletData) {
    try {
      const response = await api.post('/api/wallets', walletData);
      return response.data;
    } catch (error) {
      console.error('Error adding wallet:', error);
      throw error;
    }
  }

  /**
   * Get wallet details by ID
   * @param {number} walletId - The wallet ID
   * @returns {Promise<Object>} Wallet details
   */
  async getWallet(walletId) {
    try {
      const response = await api.get(`/api/wallets/${walletId}`);
      return response.data;
    } catch (error) {
      console.error(`[WalletSync] Error fetching wallet ${walletId}:`, error);
      throw new Error(`Failed to fetch wallet: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Get all wallets for the current user with aggregated data and market values
   * @param {Object} options - Options object
   * @param {boolean} options.fresh - If true, triggers background sync before returning data
   * @returns {Promise<Array>} Array of wallets with market values
   */
  async getWallets(options = {}) {
    try {
      const params = {};
      if (options.fresh) {
        params.fresh = 'true';
      }
      
      const response = await api.get('/api/wallets/aggregated', { params });
      return response.data.wallets || [];
    } catch (error) {
      console.error(`[WalletSync] Error fetching wallets:`, error);
      throw new Error(`Failed to fetch wallets: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Delete a wallet
   * @param {number} walletId - The wallet ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteWallet(walletId) {
    try {
      const response = await api.delete(`/api/wallets/${walletId}`);
      return response.data;
    } catch (error) {
      console.error(`[WalletSync] Error deleting wallet ${walletId}:`, error);
      throw new Error(`Failed to delete wallet: ${error.response?.data?.error || error.message}`);
    }
  }
}

export default new WalletSyncService(); 